import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';
import GenericLoader from '../../components/Loader/GenericLoader';
import DynamicTabsComponent from '../../components/DynamicTabs/DynamicTabsComponent';
import ExternalHours from '../../components/ExternalHOurs/ExternalHours';
import TasksComponent from './TasksComponent';
import RemoteOfficeHoursComponent from '../RemoteOfficeHours/RemoteOfficeHoursComponent';
import WeekendWorkingComponent from '../WeekendWorking/WeekendWorkingComponent';
import { WEEKEND_WORKING_PRIVILEGE } from '../WeekendWorking/weekendUtils';
import { TimesheetsComponentLoaderAttribute } from '../../constants';
import { useDispatch } from 'react-redux';
import { setFilterDataState } from '../../modules/filter/filterAction';
import { cardComponentExpand } from '../../modules/cardState/cardAction';
import { setSelectedMonthRangeState } from '../../modules/filter/filterAction';
import { DateObject } from 'react-multi-date-picker';
import { getUserData } from '../../services/UserService';
import KeycloakService from '../../services/KeycloakService';
import {
  getClientOfficeQuotaForUser,
  getExternalQuotaForUser,
  getWorkTravelQuotaForUser,
} from '../../admin/Services/EmployeePrivilegesService';

const hasPositiveQuota = (yearQuota) =>
  Array.isArray(yearQuota) && yearQuota.some((v) => (v || 0) > 0);

const isPrivilegeEnabled = (privileges, key) =>
  privileges && privileges[key] === 'YES';

const TasksDynamicTabs = (props) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const xyz = new URLSearchParams(location.search).get('xyz');
  const searchString = xyz ? window.atob(xyz) : '';

  const applierId = new URLSearchParams(searchString).get('applierId');
  const tabType = new URLSearchParams(searchString).get('type');
  const monthRange =
    new URLSearchParams(searchString).get('monthRange') || '[]';
  const status = new URLSearchParams(searchString).get('status');

  const [showError, setShowError] = useState(false);
  const [loader, setLoader] = React.useState(true);
  const [userName, setUserName] = useState('');
  const [showTasksTab, setShowTasksTab] = useState(false);
  const [showExternalHoursRequests, setShowExternalHoursRequests] = useState(false);
  const [showClientOfficeRequests, setShowClientOfficeRequests] =
    useState(false);
  const [showWorkTravelHoursRequests, setShowWorkTravelHoursRequests] =
    useState(false);
  const [showWeekendWorking, setShowWeekendWorking] = useState(false);
  const [activeTab, setActiveTab] = React.useState(0);

  const getRange = (monthRangeArr) => {
    const startDate = new DateObject({
      year: monthRangeArr[1],
      month: monthRangeArr[0],
      day: 1,
    });
    const endDate = new DateObject({
      year: monthRangeArr[3],
      month: monthRangeArr[2],
      day: 1,
    });
    return [startDate, endDate];
  };

  const getUserName = async () => {
    const response = await getUserData(applierId);
    if (response.isError == false)
      setUserName(response.data.firstName + ' ' + response.data.lastName);
    else setUserName('');
  };

  const loadQuotaVisibility = async () => {
    const employeeId = KeycloakService.getUserDetail();
    const year = new Date().getFullYear();
    try {
      const [extRes, clientRes, workTravelRes, userRes] = await Promise.all([
        getExternalQuotaForUser(employeeId, year),
        getClientOfficeQuotaForUser(employeeId, year),
        getWorkTravelQuotaForUser(employeeId, year),
        getUserData(employeeId),
      ]);
      const extQuota = extRes?.data?.data?.externalHoursQuota?.thisYear;
      const clientQuota =
        clientRes?.data?.data?.clientOfficeHoursQuota?.thisYear;
      const workTravelQuota =
        workTravelRes?.data?.data?.workTravelHoursQuota?.thisYear;
      setShowTasksTab(
        hasPositiveQuota(extQuota) ||
          hasPositiveQuota(clientQuota) ||
          hasPositiveQuota(workTravelQuota),
      );

      const privileges =
        userRes?.isError === false ? userRes.data?.employeePrivileges : null;
      setShowExternalHoursRequests(
        isPrivilegeEnabled(privileges, 'REQUEST_EXTERNAL_HOURS'),
      );
      setShowClientOfficeRequests(
        isPrivilegeEnabled(privileges, 'REQUEST_CLIENT_OFFICE_HOURS'),
      );
      setShowWorkTravelHoursRequests(
        isPrivilegeEnabled(privileges, 'REQUEST_WORK_TRAVEL_HOURS'),
      );
      setShowWeekendWorking(
        isPrivilegeEnabled(privileges, WEEKEND_WORKING_PRIVILEGE),
      );
    } catch (e) {
      setShowTasksTab(false);
      setShowExternalHoursRequests(false);
      setShowClientOfficeRequests(false);
      setShowWorkTravelHoursRequests(false);
      setShowWeekendWorking(false);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (applierId) {
      getUserName();
    }
    loadQuotaVisibility();
  }, []);

  const resolveActiveTab = () => {
    const tabKeys = [];
    if (showTasksTab) tabKeys.push('TASKS');
    tabKeys.push('REMOTE_OFFICE');
    if (showExternalHoursRequests) tabKeys.push('EXTERNAL');
    if (showClientOfficeRequests) tabKeys.push('CLIENT_OFFICE');
    if (showWorkTravelHoursRequests) tabKeys.push('WORK_TRAVEL');
    if (showWeekendWorking) tabKeys.push('WEEKEND_WORKING');

    if (
      tabType === 'WEEKEND_WORKING' ||
      tabType === 'EXTERNAL' ||
      tabType === 'CLIENT_OFFICE' ||
      tabType === 'WORK_TRAVEL' ||
      tabType === 'REMOTE_OFFICE'
    ) {
      const idx = tabKeys.indexOf(tabType);
      return idx >= 0 ? idx : 0;
    }
    return 0;
  };

  useEffect(() => {
    if (loader) return;
    if (tabType) {
      setActiveTab(resolveActiveTab());
      dispatch(
        setFilterDataState({
          employeeId: applierId,
          status: status,
          monthRange: JSON.parse(monthRange),
        }),
      );
      dispatch(setSelectedMonthRangeState(getRange(JSON.parse(monthRange))));
      dispatch(cardComponentExpand());
    } else {
      setActiveTab(0);
    }
  }, [
    location,
    userName,
    showTasksTab,
    showExternalHoursRequests,
    showClientOfficeRequests,
    showWorkTravelHoursRequests,
    showWeekendWorking,
    loader,
  ]);

  const ManageTabs = [];
  if (showTasksTab) {
    ManageTabs.push({
      title: 'Tasks',
      index: ManageTabs.length,
      component: <TasksComponent />,
    });
  }
  ManageTabs.push({
    title: 'Remote Office Hours',
    index: ManageTabs.length,
    component: <RemoteOfficeHoursComponent />,
  });
  if (showExternalHoursRequests) {
    ManageTabs.push({
      title: 'External Hours Requests',
      index: ManageTabs.length,
      component: <ExternalHours requestCategory="EXTERNAL" />,
    });
  }
  if (showClientOfficeRequests) {
    ManageTabs.push({
      title: 'Client Office Hours Requests',
      index: ManageTabs.length,
      component: <ExternalHours requestCategory="CLIENT_OFFICE" />,
    });
  }
  if (showWorkTravelHoursRequests) {
    ManageTabs.push({
      title: 'Work Travel Hours Requests',
      index: ManageTabs.length,
      component: <ExternalHours requestCategory="WORK_TRAVEL" />,
    });
  }
  if (showWeekendWorking) {
    ManageTabs.push({
      title: 'Weekend Working Hours',
      index: ManageTabs.length,
      component: <WeekendWorkingComponent />,
    });
  }

  return (
    <div>
      {loader ? (
        <div>
          <GenericLoader
            rectAttributes={TimesheetsComponentLoaderAttribute}
            showError={showError}
            setShowError={setShowError}
          />
        </div>
      ) : (
        <Box className="main-content-overlay">
          <div className="main-content-div">
            <DynamicTabsComponent
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              TabsList={ManageTabs}
            />
          </div>
        </Box>
      )}
    </div>
  );
};

export default TasksDynamicTabs;
